'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Zap, Target, Clock, RefreshCw, AlertCircle, TrendingUp, BarChart2, CheckCircle, XCircle } from 'lucide-react';
import { useMarketData } from '@/contexts/MarketDataContext';

interface AISignal {
  id: string;
  symbol: string;
  type: 'BUY' | 'SELL';
  strategy: string;
  entry: string;
  target: string;
  sl: string;
  confidence: number;
  timeframe: string;
  rationale?: string;
}

interface RecentOutcome {
  symbol: string;
  type: string;
  strategy: string | null;
  entry: number;
  target: number;
  sl: number;
  actualEntry: number | null;
  actualExit: number | null;
  outcome: string | null;
  pnlPct: number | null;
  resolvedAt: string | null;
  generatedAt: string;
}

interface AccuracyStats {
  winRate: number | null;
  totalSignals: number;
  recentSignals: RecentOutcome[];
}

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export default function AISignalsPanel() {
  const { ticks, indices } = useMarketData();
  const [signals, setSignals] = useState<AISignal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [accuracy, setAccuracy] = useState<AccuracyStats>({ winRate: null, totalSignals: 0, recentSignals: [] });
  const [showHistory, setShowHistory] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchSignals = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/ai/trade-signals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticks, indices }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (Array.isArray(data.signals) && data.signals.length > 0) {
        setSignals(data.signals as AISignal[]);
        setLastUpdated(data.generatedAt ?? Date.now());
      }
      if (data.accuracy) {
        setAccuracy(data.accuracy);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch signals');
    } finally {
      setIsLoading(false);
    }
  }, [ticks, indices, isLoading]);

  // Fetch accuracy stats on mount (even before market data)
  useEffect(() => {
    fetch('/api/ai/trade-signals')
      .then((r) => r.json())
      .then((d) => { if (d.accuracy) setAccuracy(d.accuracy); })
      .catch(() => {});
  }, []);

  // Initial fetch once market data is available
  useEffect(() => {
    if (hasFetchedRef.current) return;
    const hasData = Object.keys(ticks).length > 0;
    if (!hasData) return;
    hasFetchedRef.current = true;
    fetchSignals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticks]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (Object.keys(ticks).length > 0) {
        fetchSignals();
      }
    }, REFRESH_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticks, indices]);

  const formatTimeAgo = (ts: number | string) => {
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  const displayWinRate = accuracy.winRate !== null ? accuracy.winRate : null;

  return (
    <div className="card-elevated p-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center">
            <Zap size={12} className="text-primary" />
          </div>
          <h2 className="text-sm font-semibold text-foreground">AI Market Brain</h2>
        </div>
        <div className="flex items-center gap-2">
          {!isLoading && (
            <button
              onClick={fetchSignals}
              className="p-1 rounded hover:bg-muted/60 transition-colors"
              title="Refresh signals"
            >
              <RefreshCw size={11} className="text-muted-foreground" />
            </button>
          )}
          <div className="flex items-center gap-1.5">
            {isLoading ? (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            ) : (
              <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-green" />
            )}
            <span className="text-2xs text-muted-foreground">
              {isLoading
                ? 'Analyzing…'
                : displayWinRate !== null
                ? `${displayWinRate}% win rate`
                : 'Live signals'}
            </span>
          </div>
        </div>
      </div>

      {/* Accuracy Stats Bar */}
      {(accuracy.totalSignals > 0 || accuracy.winRate !== null) && (
        <div className="mb-3 p-2.5 rounded-lg bg-muted/30 border border-border">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <BarChart2 size={9} />
              Signal Accuracy
            </span>
            {accuracy.recentSignals.length > 0 && (
              <button
                onClick={() => setShowHistory((v) => !v)}
                className="text-2xs text-primary hover:underline"
              >
                {showHistory ? 'Hide' : 'History'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {displayWinRate !== null && (
              <div className="flex items-center gap-1">
                <TrendingUp size={11} className="text-positive" />
                <span className="text-sm font-bold text-positive">{displayWinRate}%</span>
                <span className="text-2xs text-muted-foreground">win rate</span>
              </div>
            )}
            {accuracy.totalSignals > 0 && (
              <div className="text-2xs text-muted-foreground">
                {accuracy.totalSignals} resolved signal{accuracy.totalSignals !== 1 ? 's' : ''}
              </div>
            )}
            {displayWinRate !== null && (
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-positive rounded-full transition-all"
                  style={{ width: `${displayWinRate}%` }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signal History */}
      {showHistory && accuracy.recentSignals.length > 0 && (
        <div className="mb-3 space-y-1.5 max-h-40 overflow-y-auto scrollbar-thin">
          <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Recent Outcomes</p>
          {accuracy.recentSignals.map((r, i) => (
            <div key={i} className="flex items-center justify-between p-2 rounded-md bg-muted/20 border border-border text-2xs">
              <div className="flex items-center gap-1.5 min-w-0">
                {r.outcome === 'WIN' ? (
                  <CheckCircle size={10} className="text-positive shrink-0" />
                ) : (
                  <XCircle size={10} className="text-negative shrink-0" />
                )}
                <span className="font-semibold text-foreground truncate">{r.symbol}</span>
                <span className={`px-1 rounded font-bold ${r.type === 'BUY' ? 'text-positive' : 'text-negative'}`}>
                  {r.type}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-muted-foreground">
                  E: ₹{r.entry.toFixed(0)} → T: ₹{r.target.toFixed(0)}
                </span>
                {r.actualExit !== null && (
                  <span className="text-muted-foreground">
                    Exit: ₹{r.actualExit.toFixed(0)}
                  </span>
                )}
                {r.pnlPct !== null && (
                  <span className={r.pnlPct >= 0 ? 'text-positive font-semibold' : 'text-negative font-semibold'}>
                    {r.pnlPct >= 0 ? '+' : ''}{r.pnlPct.toFixed(2)}%
                  </span>
                )}
                <span className="text-muted-foreground/60">
                  {formatTimeAgo(r.generatedAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="space-y-2 flex-1 overflow-y-auto scrollbar-thin">
        {/* Loading skeleton */}
        {isLoading && signals.length === 0 && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-3 rounded-lg border border-border bg-muted/20 animate-pulse">
                <div className="h-3 bg-muted rounded w-2/3 mb-2" />
                <div className="h-2 bg-muted rounded w-full mb-1" />
                <div className="h-2 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-6 gap-2 text-center">
            <AlertCircle size={20} className="text-negative/70" />
            <p className="text-xs text-muted-foreground">Unable to generate signals</p>
            <p className="text-2xs text-muted-foreground/60">{error}</p>
            <button
              onClick={fetchSignals}
              className="mt-1 text-2xs text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Signal cards */}
        {signals.map((s) => (
          <div
            key={s.id}
            className={`p-3 rounded-lg border transition-all hover:border-primary/30 cursor-pointer ${
              s.type === 'BUY' ?'bg-positive-subtle border-positive/20' :'bg-negative-subtle border-negative/20'
            }`}
          >
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div>
                <span className="text-xs font-bold text-foreground">{s.symbol}</span>
                <span
                  className={`ml-2 text-2xs font-bold px-1.5 py-0.5 rounded ${
                    s.type === 'BUY' ?'bg-positive/20 text-positive' :'bg-negative/20 text-negative'
                  }`}
                >
                  {s.type}
                </span>
              </div>
              {lastUpdated && (
                <span className="text-2xs text-muted-foreground flex items-center gap-1 shrink-0">
                  <Clock size={9} />
                  {formatTimeAgo(lastUpdated)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-2xs text-muted-foreground">
              <span>
                Entry: <span className="text-foreground font-tabular font-semibold">₹{s.entry}</span>
              </span>
              <span className="flex items-center gap-0.5 text-positive">
                <Target size={9} />₹{s.target}
              </span>
              <span className="text-negative">SL: ₹{s.sl}</span>
            </div>
            {s.rationale && (
              <p className="text-2xs text-muted-foreground/70 mt-1 leading-relaxed line-clamp-2">
                {s.rationale}
              </p>
            )}
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-2xs text-muted-foreground">
                {s.strategy} • {s.timeframe}
              </span>
              <div className="flex items-center gap-1">
                <div className="w-12 h-1 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${s.confidence}%` }}
                  />
                </div>
                <span className="text-2xs font-semibold text-primary">{s.confidence}%</span>
              </div>
            </div>
          </div>
        ))}

        {/* Empty state (after load, no signals) */}
        {!isLoading && !error && signals.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
            <Zap size={20} className="text-muted-foreground/40" />
            <p className="text-xs text-muted-foreground">Waiting for market data…</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="pt-3 border-t border-border mt-3">
        <p className="text-2xs text-muted-foreground text-center">
          AI signals are educational only. Not SEBI investment advice.
        </p>
      </div>
    </div>
  );
}